<template>
  <div class="white-space-prewrap">
    <span v-for="(node, i) in splitText" :key="i">
      <span v-if="node.isLink || node.isEmail">
        {{ node.prefix
        }}<a :href="node.href" target="_blank" rel="noopener noreferrer">
          {{ node.display }} </a
        >{{ node.suffix }}
      </span>
      <span
        v-else
        :class="{ 'opacity-80': !darker, 'opacity-100': fullOpacity }"
      >
        {{ node.text }}
      </span>
    </span>
  </div>
</template>

<script setup>
import { computed, toRef } from "vue";

const props = defineProps({
  text: {
    type: String,
    required: true,
  },
  darker: Boolean,
  fullOpacity: Boolean,
});

const text = toRef(props, "text");

// Use non-global regexes for .test()
const urlTestRe = /^(https?:\/\/[^\s]+)$/i;
const emailTestRe = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/i;

// Split on emails or URLs (global is fine here)
const combinedRegex =
  /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b|(https?:\/\/[^\s"]+)/gi;

// Characters that often wrap tokens
const LEADERS = `([{<"'“‘`;
const TRAILERS = `)]}>"'”’.,;:!?`;

// Pull off leading/trailing wrappers into prefix/suffix, leave middle as core
function splitAffixes(raw) {
  if (!raw) return { prefix: "", core: "", suffix: "" };

  let start = 0;
  let end = raw.length;

  // strip leading wrappers
  while (start < end && LEADERS.includes(raw[start])) start++;

  // special-case ")." → ")" before trailing strip (prevents losing the dot)
  if (end - start >= 2 && raw[end - 2] === ")" && raw[end - 1] === ".") {
    end--; // keep ")" as trailer, "." becomes part of suffix (we'll strip normally below)
  }

  // strip trailing wrappers/punct
  while (end > start && TRAILERS.includes(raw[end - 1])) end--;

  const prefix = raw.slice(0, start);
  const core = raw.slice(start, end);
  const suffix = raw.slice(end);

  return { prefix, core, suffix };
}

// For hrefs we want to trim any leftover trailing punctuation just in case
function cleanForHref(core) {
  // Remove any remaining trailing wrappers/punct defensively
  return core.replace(/[)\]}>"'”’.,;:!?]+$/g, "");
}

const splitText = computed(() => {
  const parts = text.value
    .replace(combinedRegex, "|||$&|||")
    .split("|||")
    .filter(Boolean);

  return parts.map(raw => {
    const { prefix, core, suffix } = splitAffixes(raw);

    const isUrl = urlTestRe.test(core);
    const isEmail = emailTestRe.test(core);

    const href = isUrl
      ? cleanForHref(core)
      : isEmail
        ? `mailto:${cleanForHref(core)}`
        : null;

    return {
      // for rendering
      prefix, // e.g. "("
      suffix, // e.g. ")."
      text: raw, // original chunk (if you ever need it)
      display: core, // what to show inside the <a>
      isLink: isUrl,
      isEmail,
      href,
    };
  });
});
</script>
